/**
 * 发薪日工作流页（**F 域 F3** · `/paydays` · `DR-052` · 批 87）
 *
 * 依据：`F域PRD` §4.7（触发 / 预演 / 落账）· `AC-F3.1`–`F3.14`
 *
 * ## 🔴 本页**不自动做任何事**
 *
 * 打开页面只发一次只读请求（候选 + 历史）。预演要用户点「分配」才发；落账要用户点
 * 「确认分配」才发（`AC-F3.1` / `AC-F3.5`）。**没有定时器、没有自动触发**。
 *
 * ## 🔴 改额 → 重新预演（500ms 防抖）
 *
 * 「本次分配后可花」只能由服务端算（`AC-F3.9` / `AC-P1.2`）⇒ 用户改完额度后，
 * 本页把新额度**再发给预演接口**（接口接受 `allocations`，`DR-052` D3），拿到新结论再显示。
 * ⚠️ 这段在 effect 里，`renderToStaticMarkup` 证不了（铁律 43）—— 由冒烟脚本覆盖。
 */

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  formatMoneyText,
  type Cents,
  type PaydayCandidateView,
  type PaydayConfirmResultView,
} from '@jiaying/shared';
import { ApiError } from '../api/http.js';
import {
  confirmPayday,
  listPaydayCandidates,
  listPaydayHistory,
  previewPayday,
} from '../api/endpoints.js';
import {
  EMPTY_DRAFT,
  candidateSubtitle,
  canConfirm,
  confirmAllocations,
  confirmSummary,
  draftFromPreview,
  failedRows,
  paydayText,
  previewAllocations,
  withAmount,
  type PaydayDraft,
} from '../features/payday.js';
import { PaydayPreviewPanel } from '../ui/PaydayPreviewPanel.js';

export function PaydayPage(): ReactElement {
  const qc = useQueryClient();
  const candidates = useQuery({ queryKey: ['paydayCandidates'], queryFn: listPaydayCandidates });
  const history = useQuery({ queryKey: ['paydayHistory'], queryFn: listPaydayHistory });

  const [active, setActive] = useState<PaydayCandidateView | null>(null);
  const [draft, setDraft] = useState<PaydayDraft>(EMPTY_DRAFT);
  /** 用户刚改完的额度（防抖输入） */
  const [pendingDraft, setPendingDraft] = useState<
    { goalId: string; amountCents: number }[] | undefined
  >(undefined);
  /** 真正发给预演的额度（500ms 后从 pendingDraft 落下来） */
  const [pendingAllocations, setPendingAllocations] = useState<
    { goalId: string; amountCents: number }[] | undefined
  >(undefined);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<PaydayConfirmResultView | null>(null);

  /** 只在"打开另一笔工资"时用建议值重置草稿；用户改过就不再覆盖 */
  const seededFor = useRef<string | null>(null);

  const preview = useQuery({
    queryKey: ['paydayPreview', active?.transactionId ?? '', pendingAllocations ?? 'default'],
    enabled: active !== null,
    queryFn: () => previewPayday(active?.transactionId ?? '', pendingAllocations),
  });

  // 首次拿到预演 ⇒ 按建议初始化草稿（之后用户改的值不被覆盖）
  useEffect(() => {
    if (active === null || preview.data === undefined) return;
    if (seededFor.current === active.transactionId) return;
    seededFor.current = active.transactionId;
    setDraft(draftFromPreview(preview.data));
  }, [active, preview.data]);

  // 改额防抖：500ms 后才把新额度交给预演查询（连点不会打出一串请求）
  useEffect(() => {
    if (pendingDraft === undefined) return;
    const timer = setTimeout(() => setPendingAllocations(pendingDraft), 500);
    return () => clearTimeout(timer);
  }, [pendingDraft]);

  const confirmMut = useMutation({
    mutationFn: () =>
      confirmPayday(active?.transactionId ?? '', confirmAllocations(draft)),
    onSuccess: (res) => {
      setOutcome(res);
      setError(null);
      setActive(null);
      seededFor.current = null;
      setPendingDraft(undefined);
      setPendingAllocations(undefined);
      void qc.invalidateQueries({ queryKey: ['paydayCandidates'] });
      void qc.invalidateQueries({ queryKey: ['paydayHistory'] });
      void qc.invalidateQueries({ queryKey: ['goals'] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
      void qc.invalidateQueries({ queryKey: ['transactions'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  function openCandidate(c: PaydayCandidateView): void {
    setError(null);
    setOutcome(null);
    seededFor.current = null;
    setDraft(EMPTY_DRAFT);
    setPendingDraft(undefined);
    setPendingAllocations(undefined);
    setActive(c);
  }

  function changeAmount(goalId: string, cents: number): void {
    const next = withAmount(draft, goalId, cents);
    setDraft(next);
    setPendingDraft(previewAllocations(next));
  }

  const items = candidates.data?.items ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="payday-page">
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            发薪日分配
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            工资到账后，把「必付预留 + 目标攒钱 + 剩下可花」一次看清楚。
            分配**不会**自动发生 —— 你确认之前不会写任何数据。
          </Typography>

          {error !== null && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="payday-error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {outcome !== null && (
            <Alert
              severity={outcome.failed > 0 ? 'warning' : 'success'}
              sx={{ mb: 2 }}
              data-testid="payday-outcome"
              onClose={() => setOutcome(null)}
            >
              <Typography variant="body2">{confirmSummary(outcome)}</Typography>
              <Typography variant="body2">
                本次分配后可花 {formatMoneyText(outcome.distributableCents as Cents, candidates.data?.currency ?? 'CNY')}
              </Typography>
              {failedRows(outcome).map((r) => (
                <Typography key={r.goalId} variant="caption" display="block">
                  {r.goalName}：{r.failReason ?? '失败'}
                </Typography>
              ))}
            </Alert>
          )}

          {candidates.isPending ? (
            <CircularProgress size={20} />
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="payday-empty">
              目前没有待分配的工资。记一笔「工资」收入后，这里会出现提示。
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />}>
              {items.map((c) => (
                <Stack
                  key={c.transactionId}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ py: 1 }}
                  data-testid={`payday-candidate-${c.transactionId}`}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {c.summary ?? '工资'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {candidateSubtitle(c)}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => openCandidate(c)}
                    data-testid={`payday-open-${c.transactionId}`}
                  >
                    分配
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            分配历史
          </Typography>
          {history.isPending ? (
            <CircularProgress size={20} />
          ) : (history.data?.items.length ?? 0) === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="payday-empty-history">
              还没有分配过。
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />}>
              {history.data?.items.slice(0, 50).map((h) => (
                <Stack key={h.id} direction="row" spacing={2} alignItems="center" sx={{ py: 0.75 }}>
                  <Typography variant="body2" sx={{ width: 90 }} color="text.secondary">
                    {paydayText(h.payday)}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                    {h.goalName ?? '（已删除的目标）'}
                  </Typography>
                  <Chip
                    size="small"
                    label={h.status === 'confirmed' ? '已分配' : '失败'}
                    color={h.status === 'confirmed' ? 'success' : 'warning'}
                    variant={h.status === 'confirmed' ? 'filled' : 'outlined'}
                  />
                  <Typography variant="body2">
                    {formatMoneyText(h.actualCents, history.data?.currency ?? 'CNY')}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ── 预演对话框 ── */}
      <Dialog open={active !== null} onClose={() => setActive(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          分配这笔工资{active === null ? '' : ` · ${paydayText(active.payday)}`}
        </DialogTitle>
        <DialogContent>
          {preview.isPending && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {preview.isError && (
            <Alert severity="error">
              {preview.error instanceof ApiError ? preview.error.message : String(preview.error)}
            </Alert>
          )}
          {preview.data !== undefined && (
            <PaydayPreviewPanel
              preview={preview.data}
              draft={draft}
              onAmountChange={changeAmount}
              disabled={confirmMut.isPending}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActive(null)} disabled={confirmMut.isPending}>
            这次不分配
          </Button>
          <Button
            variant="contained"
            disabled={confirmMut.isPending || preview.data === undefined || !canConfirm(draft)}
            onClick={() => confirmMut.mutate()}
            data-testid="payday-confirm"
          >
            确认分配
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
